import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Button,
  Input,
  Label,
  LayerCard,
  Select,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { HouseIcon, BuildingsIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { Building, Room, RoomType, RoomStatus } from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  Form Data                                                          */
/* ------------------------------------------------------------------ */

interface RoomFormData {
  name: string
  capacity: number
  type: RoomType
  photoUrl: string
  floor: string
  status: RoomStatus
}

const emptyForm: RoomFormData = {
  name: '',
  capacity: 1,
  type: 'meeting',
  photoUrl: '',
  floor: '',
  status: 'available',
}

/* ------------------------------------------------------------------ */
/*  RoomFormPage                                                        */
/* ------------------------------------------------------------------ */

export function RoomFormPage() {
  const { buildingId, roomId } = useParams<{ buildingId: string; roomId: string }>()
  const isEdit = Boolean(roomId)
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [form, setForm] = useState<RoomFormData>(emptyForm)
  const [buildingName, setBuildingName] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!buildingId) return

    async function fetchData() {
      try {
        const token = await getAccessTokenSilently()

        // Fetch building name for breadcrumbs
        const building = await apiRequest<Building>(`/api/buildings/${buildingId}`, { token })
        setBuildingName(building.name)

        // Fetch room data for edit mode
        if (roomId) {
          const rooms = await apiRequest<Room[]>(`/api/buildings/${buildingId}/rooms`, { token })
          const room = rooms.find((r) => r.id === roomId)
          if (room) {
            setForm({
              name: room.name,
              capacity: room.capacity,
              type: room.type,
              photoUrl: room.photoUrl ?? '',
              floor: room.floor ?? '',
              status: room.status,
            })
          } else {
            setError('Room not found')
          }
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    void fetchData()
  }, [buildingId, roomId, getAccessTokenSilently, t])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    try {
      const token = await getAccessTokenSilently()
      const payload = {
        ...form,
        photoUrl: form.photoUrl || null,
        floor: form.floor || null,
      }

      if (isEdit) {
        await apiRequest(`/api/buildings/${buildingId}/rooms/${roomId}`, {
          method: 'PUT',
          token,
          body: payload,
        })
        toasts.add({ variant: 'success', title: t('rooms.updateSuccess') })
      } else {
        await apiRequest(`/api/buildings/${buildingId}/rooms`, {
          method: 'POST',
          token,
          body: payload,
        })
        toasts.add({ variant: 'success', title: t('rooms.createSuccess') })
      }
      navigate('/buildings')
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<BuildingsIcon size={14} />} href="/buildings">
              {t('buildings.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            {buildingName && (
              <>
                <Breadcrumbs.Link href="/buildings">{buildingName}</Breadcrumbs.Link>
                <Breadcrumbs.Separator />
              </>
            )}
            <Breadcrumbs.Current>
              {isEdit ? t('rooms.editRoom') : t('rooms.createRoom')}
            </Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={isEdit ? t('rooms.editRoom') : t('rooms.createRoom')}
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && (
        <LayerCard>
          <LayerCard.Primary>
            <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <Label>{t('rooms.name')}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label>{t('rooms.capacity')}</Label>
                  <Input
                    type="number"
                    min={1}
                    value={String(form.capacity)}
                    onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>{t('rooms.type')}</Label>
                  <Select
                    value={form.type}
                    onValueChange={(v) => {
                      if (v) setForm({ ...form, type: v as RoomType })
                    }}
                  >
                    <Select.Option value="meeting">{t('rooms.type.meeting')}</Select.Option>
                    <Select.Option value="phone-booth">{t('rooms.type.phone-booth')}</Select.Option>
                    <Select.Option value="event">{t('rooms.type.event')}</Select.Option>
                    <Select.Option value="private-office">
                      {t('rooms.type.private-office')}
                    </Select.Option>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label>{t('rooms.floor')}</Label>
                  <Input
                    value={form.floor}
                    onChange={(e) => setForm({ ...form, floor: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label>{t('rooms.status')}</Label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => {
                      if (v) setForm({ ...form, status: v as RoomStatus })
                    }}
                  >
                    <Select.Option value="available">{t('rooms.status.available')}</Select.Option>
                    <Select.Option value="occupied">{t('rooms.status.occupied')}</Select.Option>
                    <Select.Option value="maintenance">
                      {t('rooms.status.maintenance')}
                    </Select.Option>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <Label>{t('rooms.photoUrl')}</Label>
                <Input
                  value={form.photoUrl}
                  onChange={(e) => setForm({ ...form, photoUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={() => navigate('/buildings')}>
                  {t('rooms.cancel')}
                </Button>
                <Button variant="primary" type="submit" loading={submitting}>
                  {t('rooms.save')}
                </Button>
              </div>
            </form>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
